import { lazy, Suspense, useContext, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { isMockReplay } from "@/features/oio/mock/mockMode"
import { isOiaMock } from "@/features/oia/mock/oiaMockMode"
import { Icon } from "@/components/Icon"
import { useCases } from "@/features/cases/hooks/useCases"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { toCaseVM } from "@/features/cases/case-view"
import { useShellStore } from "@/shell/shell.store"
import { confirmAction } from "@/shell/confirm.store"
import { ThreadContext } from "@/providers/ThreadProvider"
import { serviceForThread, type ServiceId } from "@/services/services.registry"
import { OIOWorkspace } from "@/features/oio/components/OIOWorkspace"
import { OIORewriteProvider } from "@/features/oio/providers/OIORewriteProvider"

// Lazy so the appeal workspace is its own chunk: OIO officers never download
// it, and it loads only when an OIA case is opened. Keep it lazy — a static
// import here would put the whole OIA bundle into the shell chunk.
const OIAWorkspace = lazy(() =>
  import("@/features/oia/components/OIAWorkspace").then((m) => ({
    default: m.OIAWorkspace,
  })),
)

// Lazy AND dev-gated so the mock chunk (incl. the ~60KB recorded fixture) is
// dropped from the PRODUCTION bundle entirely — import.meta.env.DEV is a
// compile-time constant, so in a prod build this ternary is `false ? … : …`
// and Rollup tree-shakes the dynamic import away. The mock is a local-debug
// tool only; deployed environments never ship or load it.
const MockDraftingRoute = import.meta.env.DEV
  ? lazy(() =>
      import("@/features/oio/mock/MockDraftingRoute").then((m) => ({
        default: m.MockDraftingRoute,
      })),
    )
  : (() => null)

// The OIA mock (?mock=oia): lazy and dev-gated exactly as above, so its chunk
// (the synthetic appeal, its rendered order and the replay engine) is dropped
// from the production bundle. See features/oia/mock/oiaMockMode.ts.
const MockOiaDraftingRoute = import.meta.env.DEV
  ? lazy(() =>
      import("@/features/oia/mock/MockOiaDraftingRoute").then((m) => ({
        default: m.MockOiaDraftingRoute,
      })),
    )
  : (() => null)

export function DraftingRoute() {
  // ORDER TYPE FIRST, mock second. The mock replay is an OIO fixture driven
  // through the real OIOWorkspace; if it ran for an appeal it would mount OIO
  // machinery on an OIA case. So an OIA case always gets its own route, even
  // with ?mock=aditya on the URL. The context is read null-safely (not via
  // useThreadContext, which throws without a provider): with no provider or no
  // case the route resolves to OIO, so the mock replay still opens.
  const activeThread = useContext(ThreadContext)?.activeThread ?? null

  // The OIA MOCK (?mock=oia) is the one exception, checked before the order
  // type: it opens its own synthetic appeal in its own ThreadContext and
  // mounts nothing bound to the real active case (no OIO machinery, no
  // co-agent, no writes), so whichever case is open underneath is irrelevant.
  // Latched per page load; dev only (features/oia/mock/oiaMockMode.ts).
  if (isOiaMock()) {
    return (
      <Suspense fallback={null}>
        <MockOiaDraftingRoute />
      </Suspense>
    )
  }
  if (serviceForThread(activeThread).serviceId !== "oio") {
    return <RealDraftingRoute />
  }

  // MOCK REPLAY (?mock=aditya): a frontend-only scripted replay of a recorded
  // real run — auto-opens a synthetic client-side case (never persisted, never
  // in the Vault) and makes zero backend calls. isMockReplay() is latched per
  // page load, so this branch is stable for the page's lifetime; with the
  // param absent the real route below runs unchanged. See mock/mockMode.ts.
  if (isMockReplay()) {
    return (
      <Suspense fallback={null}>
        <MockDraftingRoute />
      </Suspense>
    )
  }
  return <RealDraftingRoute />
}

function RealDraftingRoute() {
  const navigate = useNavigate()
  const { activeCase, renameCase, deleteCase } = useCases()
  const { session } = useAuth()
  const titleRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState(activeCase?.title ?? "")

  useEffect(() => {
    setTitle(activeCase?.title ?? "")
  }, [activeCase?.id, activeCase?.title])

  if (!activeCase) {
    return (
      <>
        <div className="topbar">
          <span className="topbar__crumb">Drafting</span>
          <div className="topbar__spacer" />
          <button className="btn btn--primary btn--sm" onClick={() => navigate("/ask")}>
            <Icon name="plus" size={14} /> Open simulators
          </button>
        </div>
        <div className="empty" style={{ height: "calc(100vh - 64px)" }}>
          <div className="empty__ico">
            <Icon name="gavel" size={24} />
          </div>
          <h3>No case selected</h3>
          <p>Open a simulator to prepare a new case, or choose one from the Vault.</p>
          <button className="btn btn--primary" style={{ marginTop: 14 }} onClick={() => navigate("/ask")}>
            <Icon name="plus" size={15} /> Open simulators
          </button>
        </div>
      </>
    )
  }

  const vm = toCaseVM(activeCase, session?.user)

  const commitTitle = () => {
    const next = title.trim()
    if (next && next !== activeCase.title) void renameCase(activeCase.id, next)
    else setTitle(activeCase.title)
  }

  const requestDelete = () =>
    confirmAction({
      title: "Delete case?",
      message: `"${activeCase.title}" and its draft will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete case",
      danger: true,
      onConfirm: async () => {
        await deleteCase(activeCase.id)
        navigate("/vault")
      },
    })

  return (
    <div className="oio-route">
      <div className="topbar">
        <div className="topbar__left">
          <span className="topbar__crumb">Drafting</span>
          <Icon name="chevright" size={14} />
          <input
            ref={titleRef}
            className="topbar__title-input"
            value={title}
            aria-label="Case name"
            spellCheck={false}
            size={Math.min(Math.max(title.length, 10), 46)}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                titleRef.current?.blur()
              }
            }}
          />
          <button
            className="topbar__edit"
            title="Rename case"
            aria-label="Rename case"
            onClick={() => {
              const el = titleRef.current
              if (el) {
                el.focus()
                el.select()
              }
            }}
          >
            <Icon name="edit" size={14} />
          </button>
          <span className="topbar__sub mono" style={{ color: "var(--ink-3)" }}>
            {vm.ref}
          </span>
        </div>
        <div className="topbar__spacer" />
        <button
          className="btn btn--ghost btn--sm"
          onClick={requestDelete}
          title="Delete this case"
        >
          <Icon name="trash" size={14} /> Delete
        </button>
        <button className="btn btn--ghost btn--sm" onClick={() => navigate("/ask")}>
          <Icon name="plus" size={14} /> Open simulators
        </button>
      </div>

      <CaseWorkspace serviceId={serviceForThread(activeCase).serviceId} />
    </div>
  )
}

/**
 * One workspace per order type. OIOWorkspace must never mount for an appeal:
 * its useCoAgent({ name: "oio" }) and its effects (metadata PATCHes, findings
 * caches, the OIO rewrite session) would run against the OIA case. Switching
 * component type here is safe for the rules of hooks because AppShell keys the
 * whole subtree on the thread id, so a case switch remounts it.
 */
function CaseWorkspace({ serviceId }: { serviceId: ServiceId }) {
  switch (serviceId) {
    case "oia":
      return (
        <Suspense fallback={<WorkspaceLoading />}>
          <OIAWorkspace />
        </Suspense>
      )
    case "oio":
      return (
        <OIORewriteProvider>
          <OIOWorkspace />
        </OIORewriteProvider>
      )
    default: {
      const unhandled: never = serviceId
      return unhandled
    }
  }
}

function WorkspaceLoading() {
  return (
    <div className="empty" style={{ height: "calc(100vh - 64px)" }}>
      <span className="spinner" />
      <p style={{ marginTop: 12 }}>Opening the workspace…</p>
    </div>
  )
}
